/**
 * Text that must be read whole (the current lyrics line): never cut with "…", made smaller until it fits
 * the room it has. The box sets its font size with `var(--fit)`; the hook finds the largest --fit that
 * fits, again whenever the text or the room changes.
 */
import { useLayoutEffect, useRef } from 'react';

/** The smallest the text may become, as a fraction of its size: the lyrics line stays about as large
 *  as the user's words under it (46 px × 0.55 ≈ 25 px against 24 px). */
export const MIN_FIT = 0.55;

/** The largest scale in [min, 1] for which `fits` holds (to 0.01), by bisection; `min` if none does. */
export function largestFit(fits: (scale: number) => boolean, min = MIN_FIT): number {
  if (fits(1)) return 1;
  let lo = min;
  let hi = 1;
  while (hi - lo > 0.01) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) lo = mid;
    else hi = mid;
  }
  return lo;
}

/** Keeps the element's text inside it: sets --fit on it for `content`, and when its size changes. */
export function useFitText<T extends HTMLElement>(content: unknown) {
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      // the children's layout heights, not scrollHeight: an entering line is translated by its
      // animation, and a transform would count as overflow
      const fits = (scale: number) => {
        el.style.setProperty('--fit', String(scale));
        const used = [...el.children].reduce((h, c) => h + (c as HTMLElement).offsetHeight, 0);
        return used <= el.clientHeight + 1;
      };
      el.style.setProperty('--fit', String(largestFit(fits)));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el.parentElement ?? el);
    return () => observer.disconnect();
  }, [content]);
  return ref;
}
